import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// src/components/BannedUsersPanel.tsx
// Banned Users Management Panel - Host-only interface for ban management
import React, { useState, useEffect } from 'react';
import './BannedUsersPanel.css';
const BannedUsersPanel = () => {
    const [bannedUsers, setBannedUsers] = useState([]);
    const [connectedUsers, setConnectedUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState('');
    const [banReason, setBanReason] = useState('');
    const [loading, setLoading] = useState(false);
    // Connect to main window via BroadcastChannel
    const [channel] = useState(() => new BroadcastChannel('gridchat-banned-users'));
    useEffect(() => {
        // Request initial data from main window
        channel.postMessage({ type: 'request-ban-data' });
        // Listen for updates from main window
        channel.onmessage = (event) => {
            const { type, data } = event.data;
            switch (type) {
                case 'banned-users-data':
                    setBannedUsers(data.bannedUsers || []);
                    setConnectedUsers(data.connectedUsers || []);
                    break;
                case 'ban-success':
                    console.log('âœ… User banned successfully');
                    setSelectedUser('');
                    setBanReason('');
                    // Request updated data
                    channel.postMessage({ type: 'request-ban-data' });
                    break;
                case 'unban-success':
                    console.log('âœ… User unbanned successfully');
                    // Request updated data
                    channel.postMessage({ type: 'request-ban-data' });
                    break;
                case 'ban-error':
                    console.error('âŒ Ban error:', data.error);
                    alert(`Error: ${data.error}`);
                    break;
            }
        };
        // Request data every 5 seconds
        const interval = setInterval(() => {
            channel.postMessage({ type: 'request-ban-data' });
        }, 5000);
        return () => {
            clearInterval(interval);
            channel.close();
        };
    }, [channel]);
    const handleBanUser = () => {
        if (!selectedUser) {
            alert('Please select a user to ban');
            return;
        }
        const user = connectedUsers.find(u => u.socketId === selectedUser);
        if (!user) {
            alert('User not found');
            return;
        }
        const confirmed = window.confirm(`Are you sure you want to ban ${user.username} (${user.ipAddress})?\n\n` +
            `This will immediately disconnect them and prevent them from rejoining.`);
        if (confirmed) {
            setLoading(true);
            channel.postMessage({
                type: 'ban-user',
                data: {
                    socketId: selectedUser,
                    ipAddress: user.ipAddress,
                    username: user.username,
                    reason: banReason || 'No reason provided'
                }
            });
            setLoading(false);
        }
    };
    const handleUnbanUser = (ipAddress) => {
        const user = bannedUsers.find(u => u.ipAddress === ipAddress);
        const confirmed = window.confirm(`Are you sure you want to unban ${user?.username || 'this user'} (${ipAddress})?\n\n` +
            `They will be able to join the room again.`);
        if (confirmed) {
            setLoading(true);
            channel.postMessage({
                type: 'unban-user',
                data: { ipAddress }
            });
            setLoading(false);
        }
    };
    const handleRefresh = () => {
        channel.postMessage({ type: 'request-ban-data' });
    };
    return (_jsxs("div", { className: "banned-users-panel", children: [_jsxs("header", { className: "panel-header", children: [_jsx("h1", { children: "\u00F0\u0178\u0161\u00AB Banned Users Management" }), _jsx("button", { className: "refresh-button", onClick: handleRefresh, children: "\u00F0\u0178\u201D\u201E Refresh" })] }), _jsxs("div", { className: "panel-content", children: [_jsxs("section", { className: "ban-section", children: [_jsx("h2", { children: "Ban a User" }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "user-select", children: "Select User:" }), _jsxs("select", { id: "user-select", value: selectedUser, onChange: (e) => setSelectedUser(e.target.value), disabled: loading, children: [_jsx("option", { value: "", children: "-- Select a user --" }), connectedUsers
                                                .filter(u => !u.isHost) // Don't allow banning hosts
                                                .map(user => (_jsxs("option", { value: user.socketId, children: [user.username, " (", user.ipAddress, ")", user.isAuthenticated ? ' [Authenticated]' : ' [Guest]'] }, user.socketId)))] })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "ban-reason", children: "Reason (optional):" }), _jsx("input", { id: "ban-reason", type: "text", value: banReason, onChange: (e) => setBanReason(e.target.value), placeholder: "e.g., Harassment, Spam, etc.", disabled: loading })] }), _jsx("button", { className: "ban-button", onClick: handleBanUser, disabled: !selectedUser || loading, children: "\u00F0\u0178\u0161\u00AB Ban User" }), connectedUsers.length === 0 && (_jsx("p", { className: "no-users", children: "No users connected to ban." }))] }), _jsxs("section", { className: "banned-list-section", children: [_jsxs("h2", { children: ["Banned Users (", bannedUsers.length, ")"] }), bannedUsers.length === 0 ? (_jsx("p", { className: "no-banned", children: "No banned users." })) : (_jsx("div", { className: "banned-users-list", children: bannedUsers.map((user, index) => (_jsxs("div", { className: "banned-user-card", children: [_jsxs("div", { className: "banned-user-info", children: [_jsxs("div", { className: "banned-user-main", children: [_jsx("span", { className: "banned-username", children: user.username || 'Unknown User' }), _jsx("span", { className: "banned-ip", children: user.ipAddress })] }), _jsxs("div", { className: "banned-user-meta", children: [_jsxs("span", { className: "banned-date", children: ["Banned: ", new Date(user.bannedAt).toLocaleString()] }), _jsxs("span", { className: "banned-by", children: ["By: ", user.bannedBy] }), user.reason && (_jsxs("span", { className: "banned-reason", children: ["Reason: ", user.reason] }))] })] }), _jsx("button", { className: "unban-button", onClick: () => handleUnbanUser(user.ipAddress), disabled: loading, children: "\u00E2\u0153\u2026 Unban" })] }, index))) }))] }), _jsxs("section", { className: "connected-users-section", children: [_jsxs("h2", { children: ["Connected Users (", connectedUsers.length, ")"] }), _jsx("div", { className: "connected-users-list", children: connectedUsers.map(user => (_jsx("div", { className: "connected-user-card", children: _jsxs("div", { className: "user-info", children: [_jsxs("span", { className: "user-name", children: [user.username, user.isHost && ' ðŸ‘‘'] }), _jsx("span", { className: "user-ip", children: user.ipAddress }), _jsx("span", { className: "user-status", children: user.isAuthenticated ? 'ðŸ” Authenticated' : 'ðŸ‘¤ Guest' })] }) }, user.socketId))) })] })] })] }));
};
export default BannedUsersPanel;
